import { produce } from 'immer';
import { nanoid } from 'nanoid';
import { without, size } from 'lodash/fp';
import { Duration } from 'luxon';
import { HTTP_SERVER_TIMEOUT } from 'app/middleware/constants';
import { SIGN_OUT } from 'app/store/users/types.js';
import { extractError } from 'app/lib/error';
import { toStringFqn } from 'app/lib/utils';
import {
    UPDATE_PROJECT_OWNER_SUCCESS_PROJECT_OWNER_UPDATED,
    CREATE_SNOWFLAKE_TABLE_FOR_UPLOAD_SUCCESS_SNOWFLAKE_TABLE_FOR_UPLOAD_CREATED
} from 'app/store/__generated__/events';
import {
    ExecutionStatus,
    FAILED_TABLE_MATERIALIZATION_COMPLETED,
    JobRunStatus
} from 'app/lib/types/server';
import {
    UNLOAD_SNOWFLAKE_TABLE_FAILURE_NO_DEPLOYMENT_HISTORY,
    UNLOAD_SNOWFLAKE_TABLE_FAILURE_TIMEOUT,
    UNLOAD_SNOWFLAKE_TABLE_FAILURE_UNEXPECTED_FAILURE
} from 'app/store/__generated__/events/job';
import { COMPLETE_DATA_UPLOAD } from 'app/store/fileUpload/types.js';
import { UPDATE_PROJECT_NAME, UPDATE_VIEW_NAME } from 'app/store/entities/types.js';
import {
    INITIATE_EXPLORATION_DOWNLOAD,
    EXPLORATION_DOWNLOAD_INITIATED,
    EXPLORATION_DOWNLOAD_FAILED,
    CREATE_QUERY,
    UPDATE_QUERY,
    DELETE_QUERY,
    PROMOTE_EXPLORATION,
    CREATE_EXPLORE_TRANSFORM
} from 'app/store/exploreData/types.js';
import { DEPLOY_AS_TABLE, DEPLOY_AS_VIEW } from 'app/store/deployView/types.js';
import {
    DOWNLOAD_VIEW_FAILED,
    DOWNLOAD_VIEW_INITIATED,
    PREPARE_DOWNLOAD
} from 'app/store/downloadView/types.js';
import { RESTORE_TRANSFORMATION_DEPLOYMENT } from 'app/store/deploymentHistory/types.js';
import {
    SEND_EMAIL,
    SET_TRANSFORMATION_EMAIL_SCHEDULE,
    SET_EXPLORATION_EMAIL_SCHEDULE,
    SAVE_SHEET_SCHEDULE,
    DELETE_SCHEDULE,
    PAUSE_SCHEDULE,
    SYNC_NOW
} from 'app/store/shares/types.js';
import { INSUFFICIENT_PRIVILEGES, TABLE_ALREADY_EXISTS } from 'app/store/__generated__/errors';
import {
    DELETE_SNOWFLAKE_TABLE,
    GET_SNOWFLAKE_SOURCE_COLUMNS,
    SET_USER_SESSION_WAREHOUSE,
    SET_SNOWFLAKE_ROLE
} from 'app/store/snowflake/types.js';
import {
    SET_SNOWFLAKE_USER_CREDENTIALS_ACTION_TYPE,
    SET_SNOWFLAKE_SERVICE_ACCOUNT_CONFIGURATION_ACTION_TYPE
} from 'app/store/snowflakeConfiguration/types';
import { SET_OVERRIDE_CONFIG } from 'app/store/applicationConfig/types.js';
import { SYNC_PROJECT } from 'app/store/projects/types.js';
import { ADD_DATASETS_TO_PROJECT } from 'app/store/addViews/types.js';
import {
    CREATE_EVENT_SUBSCRIPTION,
    CREATE_SLACK_CHANNEL,
    DELETE_EVENT_SUBSCRIPTION,
    UPDATE_EVENT_SUBSCRIPTION_DELIVERY_TARGET
} from 'app/store/integrations/slack/types';
import { RENAME_NODE_GROUP } from 'app/store/flow/types.js';
import { LOAD_METRICS } from 'app/store/extractJson/types.js';
import {
    ANALYZE_SCHEMA_USAGE,
    SHOW_ANALAYZE_SCHEMA_USAGE_FAILURE_NOTIFICATION,
    SHOW_ANALAYZE_SCHEMA_USAGE_SUCCESS_NOTIFICATION
} from 'app/store/schemaUsage/types.js';
import { CREATE_SQL_VIEW, DESCRIBE_NODE_SQL } from 'app/store/notebook/types.js';
import { TABLE_MATERIALIZATION } from 'app/store/materialize/types.js';
import {
    DELETE_SNOWFLAKE_SERVICE_ACCOUNT_USER_ACTION_TYPE,
    CANCEL_JOB_RUN_ACTION_TYPE,
    DELETE_JOB_DEFINITION_ACTION_TYPE,
    DELETE_JOB_ENVIRONMENT_ACTION_TYPE,
    SET_JOB_ENVIRONMENT_ACTION_TYPE,
    SET_JOB_DEFINITION_ACTION_TYPE,
    TRIGGER_JOB_RUN_ACTION_TYPE,
    TRIGGER_JOB_RUNS_ACTION_TYPE,
    UNLOAD_JOB_FAILURE_ACTION_TYPE
} from 'app/store/jobs/types';
import {
    DELETE_USER,
    ACTIVATE_USER,
    DEACTIVATE_USER,
    REVOKE_API_KEY,
    TRANSFER_PROJECT_OWNERSHIP
} from 'app/store/admin/types.js';
import {
    EXECUTE_EXPLORE_QUERY,
    EXECUTE_ROW_COUNT_QUERY,
    CANCEL_QUERY
} from 'app/store/snowflakeQueries/types.js';
import { LOAD_VIEW_CARDINALITY } from 'app/store/columnStatistics/types.js';
import { EXCHANGE_SOURCE } from 'app/store/exchangeSource/types.js';
import { CONVERT_TO_MODEL_ACTION_TYPE } from 'app/store/projectModels/types.js';
import { VALIDATE_SNOWFLAKE_ACCESS_ACTION_TYPE } from 'app/store/access/types.js';
import { CREATE, UPDATE } from 'app/store/multiJoin/types.js';
import { DISMISS_NOTIFICATION, SHOW_NOTIFICATION_SUCCESS } from './types.js';

function createNotification(draft, params) {
    const id = nanoid();
    draft.byId[id] = { id, ...params };
    draft.unreadIds.push(id);
}

function createGenericErrorNotification(draft, actionPayload, i18nKey, i18nParams = {}) {
    const error = extractError(actionPayload);

    createNotification(draft, {
        _type: 'GenericErrorNotification',
        error,
        i18nKey,
        i18nParams
    });
}

export const INITIAL_STATE = {
    byId: {},
    unreadIds: []
};

export const notificationsReducer = produce((draft, action) => {
    switch (action.type) {
        case DISMISS_NOTIFICATION:
            draft.unreadIds = without(action.payload.ids, draft.unreadIds);
            return;

        case COMPLETE_DATA_UPLOAD.STARTED:
            createNotification(draft, {
                _type: 'FileUploadStarted'
            });
            return;

        case DOWNLOAD_VIEW_INITIATED:
        case EXPLORATION_DOWNLOAD_INITIATED:
            createNotification(draft, {
                _type: action.type,
                viewName: action.payload.viewName
            });
            return;

        case PREPARE_DOWNLOAD.SUCCEEDED:
            createNotification(draft, {
                _type: action.type,
                viewName: action.payload.viewName,
                viewUuid: action.payload.viewUuid,
                preparedDownloadFilename: action.payload.preparedDownloadFilename,
                // 'preparedDownloadDeletionDelay' is used to make sure the notification is visible for as long as the file is available
                autoHideDelay: Duration.fromISO(action.payload.preparedDownloadDeletionDelay).as(
                    'milliseconds'
                )
            });
            return;

        case SYNC_PROJECT.STARTED:
            createNotification(draft, {
                _type: 'GenericNotification',
                i18nKey: 'notifications:SyncProjectStarted'
            });
            break;
        case SYNC_PROJECT.SUCCEEDED:
            createNotification(draft, {
                _type: 'GenericSuccessNotification',
                i18nKey: 'notifications:SyncProjectSucceeded'
            });
            break;
        case SYNC_PROJECT.FAILED:
            console.error('SyncProject failed', action);
            createGenericErrorNotification(
                draft,
                action.payload,
                'notifications:SyncProjectFailed'
            );
            break;

        case CREATE_QUERY.FAILED:
            createGenericErrorNotification(
                draft,
                action.payload,
                'notifications:CreateQueryFailed'
            );
            return;

        case UPDATE_QUERY.FAILED:
            createGenericErrorNotification(
                draft,
                action.payload,
                'notifications:UpdateQueryFailed'
            );
            return;

        case ADD_DATASETS_TO_PROJECT.FAILED:
            createGenericErrorNotification(draft, action.payload, 'notifications:AddDatasetFailed');
            return;

        case DELETE_QUERY.FAILED:
            createGenericErrorNotification(
                draft,
                action.payload,
                'notifications:DeleteQueryFailed'
            );
            return;

        case SET_JOB_ENVIRONMENT_ACTION_TYPE.SUCCEEDED:
            createNotification(draft, {
                _type: 'GenericSuccessNotification',
                i18nKey: 'notifications:SetEnvironmentSucceeded',
                i18nParams: {
                    name: action.params.name,
                    context: action.params.uid ? 'update' : 'create'
                }
            });
            return;

        case SET_JOB_DEFINITION_ACTION_TYPE.SUCCEEDED:
            createNotification(draft, {
                _type: 'GenericSuccessNotification',
                i18nKey: 'notifications:SetJobDefinitionSucceeded',
                i18nParams: {
                    name: action.params.name,
                    context: action.params.uid ? 'update' : 'create'
                }
            });
            return;

        case DELETE_JOB_DEFINITION_ACTION_TYPE.SUCCEEDED:
            createNotification(draft, {
                _type: 'GenericSuccessNotification',
                i18nKey: 'jobs:dialogs.deleteJob.succeeded',
                i18nParams: { name: action.meta.name }
            });
            return;
        case DELETE_JOB_DEFINITION_ACTION_TYPE.FAILED:
            createGenericErrorNotification(draft, action.payload, 'jobs:dialogs.deleteJob.failed', {
                name: action.meta.name
            });
            return;

        case DELETE_JOB_ENVIRONMENT_ACTION_TYPE.SUCCEEDED:
            createNotification(draft, {
                _type: 'GenericSuccessNotification',
                i18nKey: 'jobs:dialogs.deleteEnv.succeeded',
                i18nParams: { name: action.meta.name }
            });
            return;
        case DELETE_JOB_ENVIRONMENT_ACTION_TYPE.FAILED:
            createGenericErrorNotification(draft, action.payload, 'jobs:dialogs.deleteEnv.failed', {
                name: action.meta.name
            });
            return;

        case DELETE_SNOWFLAKE_SERVICE_ACCOUNT_USER_ACTION_TYPE.SUCCEEDED:
            createNotification(draft, {
                _type: 'GenericSuccessNotification',
                i18nKey: 'jobs:dialogs.deleteServiceAccount.succeeded',
                i18nParams: { name: action.meta.name }
            });
            return;
        case DELETE_SNOWFLAKE_SERVICE_ACCOUNT_USER_ACTION_TYPE.FAILED:
            createGenericErrorNotification(
                draft,
                action.payload,
                'jobs:dialogs.deleteServiceAccount.failed',
                {
                    name: action.meta.name
                }
            );
            return;

        case TRIGGER_JOB_RUN_ACTION_TYPE.SUCCEEDED:
            createNotification(draft, {
                _type: 'GenericSuccessNotification',
                i18nKey: 'jobs:definitions.run.succeeded',
                i18nParams: { name: action.payload.name }
            });
            return;

        case TRIGGER_JOB_RUN_ACTION_TYPE.FAILED: {
            const { runStatus } = action.payload.failures[0];
            createGenericErrorNotification(draft, action.payload, 'jobs:definitions.run.failed', {
                name: action.meta.name,
                context: runStatus === JobRunStatus.SKIPPED ? 'skipped' : null
            });
            return;
        }

        case TRIGGER_JOB_RUNS_ACTION_TYPE.SUCCEEDED: {
            const submittedCount = size(action.payload.submitted);
            const skippedSize = size(action.payload.skipped);
            const checkFailedSize = size(action.payload.failed);

            if (submittedCount > 0) {
                createNotification(draft, {
                    _type: 'GenericSuccessNotification',
                    i18nKey: 'jobs:definitions.run.succeeded',
                    i18nParams: { count: submittedCount }
                });
            }
            if (skippedSize > 0) {
                createNotification(draft, {
                    _type: 'GenericErrorNotification',
                    i18nKey: 'jobs:definitions.run.failed_skipped',
                    i18nParams: { count: skippedSize }
                });
            }
            if (checkFailedSize > 0) {
                createNotification(draft, {
                    _type: 'GenericErrorNotification',
                    i18nKey: 'jobs:definitions.run.failed_other',
                    i18nParams: { count: checkFailedSize }
                });
            }
            return;
        }

        case TRIGGER_JOB_RUNS_ACTION_TYPE.FAILED: {
            createGenericErrorNotification(
                draft,
                action.payload,
                'jobs:definitions.run.failed_other'
            );
            return;
        }

        case CANCEL_JOB_RUN_ACTION_TYPE.SUCCEEDED:
            createNotification(draft, {
                _type: 'GenericSuccessNotification',
                i18nKey: 'jobs:dashboard.cancel.succeeded'
            });
            return;

        case CANCEL_JOB_RUN_ACTION_TYPE.FAILED:
            createGenericErrorNotification(draft, action.payload, 'jobs:dashboard.cancel.failed');
            return;

        case REVOKE_API_KEY.SUCCEEDED:
            createNotification(draft, {
                _type: 'GenericSuccessNotification',
                i18nKey: 'admin:AppTokens.revoke.succeeded',
                i18nParams: { name: action.meta.name }
            });
            return;
        case REVOKE_API_KEY.FAILED:
            createGenericErrorNotification(draft, action.payload, 'admin:AppTokens.revoke.failed', {
                name: action.meta.name
            });
            return;
        case PROMOTE_EXPLORATION.FAILED:
        case PROMOTE_EXPLORATION.REJECTED:
            createGenericErrorNotification(
                draft,
                action.payload,
                'notifications:PromoteExplorationFailed'
            );
            return;

        case PREPARE_DOWNLOAD.FAILED: {
            const error = extractError(action.payload);
            createNotification(draft, {
                _type: DOWNLOAD_VIEW_FAILED,
                viewName: action.meta.viewName,
                error
            });
            return;
        }

        case INITIATE_EXPLORATION_DOWNLOAD.SUCCEEDED:
            createNotification(draft, {
                _type: action.type,
                targetName: action.meta.targetName,
                viewName: action.meta.viewName,
                preparedDownloadFilename: action.payload.preparedDownloadFilename,
                // 'preparedDownloadDeletionDelay' is used to make sure the notification is visible for as long as the file is available
                autoHideDelay: Duration.fromISO(action.payload.preparedDownloadDeletionDelay).as(
                    'milliseconds'
                )
            });
            return;

        case INITIATE_EXPLORATION_DOWNLOAD.FAILED: {
            const error = extractError(action.payload);
            createNotification(draft, {
                _type: EXPLORATION_DOWNLOAD_FAILED,
                viewName: action.meta.viewName,
                targetName: action.meta.targetName,
                error
            });
            return;
        }

        case DOWNLOAD_VIEW_FAILED: {
            createNotification(draft, {
                _type: action.type,
                viewName: action.payload.viewName,
                error: action.payload.error
            });
            return;
        }

        case CREATE_SNOWFLAKE_TABLE_FOR_UPLOAD_SUCCESS_SNOWFLAKE_TABLE_FOR_UPLOAD_CREATED: {
            const { database, schema, droppedRowCount, errorTable } = action.payload;

            createNotification(draft, {
                _type: 'UploadDataset_Success',
                droppedRowCount,
                database,
                schema,
                errorTable
            });
            return;
        }
        case DEPLOY_AS_TABLE.PROGRESS: {
            const { database, schema, table } = action.payload;

            if (action.responseName === 'ViewPublishingInitiated') {
                createNotification(draft, {
                    _type: 'DeployTransformationInitiated',
                    id: nanoid(),
                    database,
                    schema,
                    table
                });
            }
            return;
        }

        case DEPLOY_AS_VIEW.SUCCEEDED:
        case DEPLOY_AS_TABLE.SUCCEEDED: {
            const deployedAs = action.type === DEPLOY_AS_TABLE.SUCCEEDED ? 'table' : 'view';
            const { redeploy } = action.params;
            const deployMethod = redeploy ? 're-deployed' : 'deployed';

            createNotification(draft, {
                _type: 'GenericSuccessNotification',
                i18nKey: 'deployTransformation:notifications.Succeeded',
                i18nParams: {
                    deployedAs,
                    deployMethod
                }
            });
            return;
        }

        case DEPLOY_AS_TABLE.FAILED:
        case DEPLOY_AS_VIEW.FAILED: {
            const error = extractError(action.payload);

            if (
                error._type.includes(TABLE_ALREADY_EXISTS) ||
                error._type.includes(INSUFFICIENT_PRIVILEGES)
            ) {
                return;
            }

            createNotification(draft, {
                _type: 'DeployTransformationFailed',
                error
            });
            return;
        }

        case UPDATE_VIEW_NAME.FAILED:
            createGenericErrorNotification(
                draft,
                action.payload,
                'notifications:UpdateViewNameFailed'
            );
            return;
        case RENAME_NODE_GROUP.FAILED:
            createGenericErrorNotification(
                draft,
                action.payload,
                'notifications:UpdateGroupNameFailed'
            );
            return;
        case UPDATE_PROJECT_NAME.FAILED:
            createGenericErrorNotification(
                draft,
                action.payload,
                'notifications:UpdateSpaceNameFailed'
            );
            return;
        case GET_SNOWFLAKE_SOURCE_COLUMNS.FAILED:
            createGenericErrorNotification(
                draft,
                action.payload,
                'notifications:ColumnRefreshFailed'
            );
            return;
        case SET_USER_SESSION_WAREHOUSE.SUCCEEDED: {
            const { showNotification } = action.meta;
            if (showNotification) {
                createNotification(draft, {
                    _type: 'SessionWhUpdated',
                    id: nanoid(),
                    i18nKey: 'notifications:sessionWhUpdated'
                });
            }
            break;
        }
        case SET_SNOWFLAKE_ROLE.SUCCEEDED: {
            const { showNotification } = action.meta;
            if (showNotification) {
                createNotification(draft, {
                    _type: 'SessionRoleUpdated',
                    id: nanoid(),
                    i18nKey: 'notifications:sessionRoleUpdated'
                });
            }
            break;
        }
        case SEND_EMAIL.FAILED:
        case SET_TRANSFORMATION_EMAIL_SCHEDULE.FAILED:
        case SET_TRANSFORMATION_EMAIL_SCHEDULE.REJECTED:
        case SET_EXPLORATION_EMAIL_SCHEDULE.FAILED:
        case SET_EXPLORATION_EMAIL_SCHEDULE.REJECTED:
            createGenericErrorNotification(draft, action.payload, 'notifications:SubmitMailFailed');
            return;

        case SYNC_NOW.FAILED:
            createGenericErrorNotification(
                draft,
                action.payload,
                'notifications:SyncNowGoogleSheetFailed'
            );
            return;

        case DELETE_SCHEDULE.FAILED:
            createGenericErrorNotification(
                draft,
                action.payload,
                'notifications:DeleteScheduleFailed'
            );
            return;
        case SET_OVERRIDE_CONFIG.FAILED:
            createGenericErrorNotification(draft, action.payload, 'notifications:SetConfigFailed');
            return;

        case SHOW_NOTIFICATION_SUCCESS:
            createNotification(draft, {
                _type: 'GenericSuccessNotification',
                i18nKey: action.payload.i18nKey
            });
            return;

        case SEND_EMAIL.PROGRESS:
        case SET_TRANSFORMATION_EMAIL_SCHEDULE.SUCCEEDED:
        case SET_EXPLORATION_EMAIL_SCHEDULE.SUCCEEDED:
        case SAVE_SHEET_SCHEDULE.SUCCEEDED:
        case SET_OVERRIDE_CONFIG.SUCCEEDED:
        case SYNC_NOW.PROGRESS:
        case SYNC_NOW.SUCCEEDED:
        case CREATE_EVENT_SUBSCRIPTION.SUCCEEDED:
        case CREATE_SLACK_CHANNEL.SUCCEEDED:
        case DELETE_EVENT_SUBSCRIPTION.SUCCEEDED:
        case UPDATE_EVENT_SUBSCRIPTION_DELIVERY_TARGET.SUCCEEDED:
            createNotification(draft, {
                _type: 'GenericSuccessNotification',
                i18nKey: `notifications:${action.type}`
            });
            return;

        case DELETE_SCHEDULE.SUCCEEDED:
        case PAUSE_SCHEDULE.SUCCEEDED:
            createNotification(draft, {
                _type: 'GenericSuccessNotification',
                i18nKey: `notifications:${action.type}.${action.meta.shareType}`
            });
            return;

        case UPDATE_PROJECT_OWNER_SUCCESS_PROJECT_OWNER_UPDATED: {
            createNotification(draft, {
                _type: 'GenericSuccessNotification',
                i18nKey: `notifications:${action.type}`,
                i18nParams: {
                    newOwner: action.payload.newOwner,
                    projectName: action.payload.name
                }
            });
            return;
        }
        case EXECUTE_ROW_COUNT_QUERY.FAILED:
            createGenericErrorNotification(
                draft,
                action.payload,
                'notifications:CalculateRowCountFailed'
            );
            return;

        case RESTORE_TRANSFORMATION_DEPLOYMENT.PROGRESS: {
            const { database, schema, table } = action.params;
            createNotification(draft, {
                _type: 'GenericNotification',
                i18nKey: `notifications:${action.type}`,
                i18nParams: { fqn: toStringFqn({ database, schema, table }) }
            });
            break;
        }

        case RESTORE_TRANSFORMATION_DEPLOYMENT.SUCCEEDED: {
            createNotification(draft, {
                _type: 'GenericSuccessNotification',
                i18nKey: `notifications:${action.type}`,
                i18nParams: {
                    message: action.params.message,
                    version: action.params.version
                }
            });
            return;
        }

        case ANALYZE_SCHEMA_USAGE.PROGRESS:
            createNotification(draft, {
                _type: 'AnalyzeSchemaUsageStarted',
                id: nanoid(),
                ...action.payload
            });
            return;

        case SHOW_ANALAYZE_SCHEMA_USAGE_SUCCESS_NOTIFICATION:
        case SHOW_ANALAYZE_SCHEMA_USAGE_FAILURE_NOTIFICATION:
            createNotification(draft, {
                _type: action.type,
                id: nanoid(),
                ...action.payload
            });
            return;

        case LOAD_METRICS.FAILED:
            createGenericErrorNotification(draft, action.payload);
            return;

        case LOAD_METRICS.TIMEDOUT:
        case LOAD_VIEW_CARDINALITY.TIMEDOUT:
            createNotification(draft, {
                _type: 'TimeoutNotification',
                payload: action.payload,
                i18nKey: 'workbench:operations.extractJson.errors.timeout'
            });
            break;

        case DELETE_SNOWFLAKE_TABLE.SUCCEEDED: {
            const { database, schema, table } = action.params;
            createNotification(draft, {
                _type: 'GenericSuccessNotification',
                i18nKey: 'notifications:DeleteSnowflakeTableSucceeded',
                i18nParams: { fqn: toStringFqn({ database, schema, table }) }
            });
            break;
        }

        case DELETE_SNOWFLAKE_TABLE.FAILED: {
            const { database, schema, table } = action.params;
            createGenericErrorNotification(
                draft,
                action.payload,
                'notifications:DeleteSnowflakeTableFailed',
                {
                    fqn: toStringFqn({ database, schema, table })
                }
            );
            break;
        }

        case CREATE_SQL_VIEW.SUCCEEDED: {
            createNotification(draft, {
                _type: 'NotebookSqlNodeCreated',
                viewRef: action.payload.viewRef
            });
            break;
        }

        case TABLE_MATERIALIZATION.PROGRESS: {
            createNotification(draft, {
                _type: 'GenericNotification',
                i18nKey: `notifications:${action.type}`,
                i18nParams: { table: action.payload.table }
            });
            break;
        }
        case TABLE_MATERIALIZATION.SUCCEEDED: {
            if (action.responseName === FAILED_TABLE_MATERIALIZATION_COMPLETED) {
                createGenericErrorNotification(
                    draft,
                    action.payload,
                    'notifications:TableMaterializationFailed',
                    {
                        errorMessage: action.payload.errorMessage
                    }
                );
                return;
            }
            if (action.payload?.queryStatus === ExecutionStatus.FAILED_WITH_ERROR) {
                createNotification(draft, {
                    _type: 'GenericNotification',
                    i18nKey: `notifications:${TABLE_MATERIALIZATION.SUCCEEDED}.withError`,
                    i18nParams: {
                        table: action.payload.table,
                        errorMessage: action.payload.errorMessage
                    }
                });
            } else {
                createNotification(draft, {
                    _type: 'GenericSuccessNotification',
                    i18nKey: `notifications:${TABLE_MATERIALIZATION.SUCCEEDED}.completed`,
                    i18nParams: {
                        table: action.payload.table
                    }
                });
            }
            break;
        }
        case TABLE_MATERIALIZATION.FAILED:
            createGenericErrorNotification(
                draft,
                action.payload,
                `notifications:${TABLE_MATERIALIZATION.FAILED}`
            );
            return;

        case DESCRIBE_NODE_SQL.FAILED: {
            createGenericErrorNotification(
                draft,
                action.payload,
                `notifications:${DESCRIBE_NODE_SQL.FAILED}`
            );
            return;
        }

        case ACTIVATE_USER.SUCCEEDED:
        case DEACTIVATE_USER.SUCCEEDED:
        case DELETE_USER.SUCCEEDED: {
            createNotification(draft, {
                _type: 'GenericSuccessNotification',
                i18nKey: `notifications:${action.type}`,
                i18nParams: {
                    username: action.payload.username
                }
            });
            return;
        }

        case ACTIVATE_USER.FAILED:
        case DEACTIVATE_USER.FAILED:
        case DELETE_USER.FAILED: {
            createGenericErrorNotification(draft, action.payload, `notifications:${action.type}`, {
                username: action.params.username
            });
            return;
        }

        case CREATE_EXPLORE_TRANSFORM.FAILED: {
            createGenericErrorNotification(
                draft,
                action.payload,
                'notifications:CreateExplorationTransformationFailed'
            );
            return;
        }

        case EXECUTE_EXPLORE_QUERY.ENDED:
        case EXECUTE_EXPLORE_QUERY.FAILED: {
            const { queryUuid, entityId, pathname } = action.meta;

            createNotification(draft, {
                _type: action.type,
                id: nanoid(),
                queryUuid,
                entityId,
                pathname
            });
            return;
        }

        case CANCEL_QUERY.FAILED: {
            const { id: queryUuid } = action.params;

            createGenericErrorNotification(draft, action.payload, `notifications:${action.type}`, {
                queryUuid
            });
            return;
        }

        case SIGN_OUT:
            return INITIAL_STATE;
        case SET_SNOWFLAKE_USER_CREDENTIALS_ACTION_TYPE.SUCCEEDED:
        case SET_SNOWFLAKE_SERVICE_ACCOUNT_CONFIGURATION_ACTION_TYPE.SUCCEEDED:
            createNotification(draft, {
                _type: 'GenericSuccessNotification',
                i18nKey: `notifications:${action.type}`
            });
            break;

        case UNLOAD_SNOWFLAKE_TABLE_FAILURE_NO_DEPLOYMENT_HISTORY:
        case UNLOAD_SNOWFLAKE_TABLE_FAILURE_TIMEOUT:
        case UNLOAD_SNOWFLAKE_TABLE_FAILURE_UNEXPECTED_FAILURE: {
            createNotification(draft, {
                _type: UNLOAD_JOB_FAILURE_ACTION_TYPE,
                i18nKey: `notifications:${UNLOAD_JOB_FAILURE_ACTION_TYPE}`,
                jobRunUid: action._context?.jobRunUid
            });
            return;
        }

        case EXCHANGE_SOURCE.SUCCEEDED:
            if (action.meta.sourceId) {
                createNotification(draft, {
                    _type: 'ExchangeSourceSuccess',
                    id: nanoid(),
                    sourceName: action.meta.sourceName,
                    targetName: action.meta.targetName
                });
            } else {
                createNotification(draft, {
                    _type: 'ExchangeSourceDisconnected',
                    id: nanoid(),
                    targetName: action.meta.targetName
                });
            }
            return;
        case EXCHANGE_SOURCE.FAILED:
            createGenericErrorNotification(
                draft,
                action.payload,
                'notifications:ExchangeSourceFailed'
            );
            return;

        case TRANSFER_PROJECT_OWNERSHIP.SUCCEEDED:
            createNotification(draft, {
                _type: 'GenericSuccessNotification',
                i18nKey: 'notifications:TransferOwnershipSucceeded',
                i18nParams: {
                    count: action.params.spaceUids.length,
                    newOwner: action.params.newOwner
                }
            });
            return;

        case CONVERT_TO_MODEL_ACTION_TYPE.SUCCEEDED:
            createNotification(draft, {
                _type: 'GenericSuccessNotification',
                i18nKey: 'notifications:ConvertToModelSucceeded',
                i18nParams: { name: action.meta.viewName }
            });
            return;
        case CONVERT_TO_MODEL_ACTION_TYPE.FAILED:
        case CONVERT_TO_MODEL_ACTION_TYPE.REJECTED: {
            const error = extractError(action.payload);
            createNotification(draft, {
                _type: 'ConvertToModelFailed',
                error,
                name: action.meta.viewName
            });
            return;
        }
        case VALIDATE_SNOWFLAKE_ACCESS_ACTION_TYPE.SUCCEEDED: {
            if (action.meta.showNotification) {
                createNotification(draft, {
                    _type: 'ProjectErrors',
                    existingProjectErrorNodeUids: action.meta.projectErrorNodeUids
                });
            }
            return;
        }

        case CREATE.FAILED:
            createGenericErrorNotification(
                draft,
                action.payload,
                `notifications:CreateMultiJoinFailed`
            );
            return;

        case UPDATE.FAILED:
            createGenericErrorNotification(
                draft,
                action.payload,
                `notifications:UpdateMultiJoinFailed`
            );
            return;

        default:
            if (action.payload?.code === HTTP_SERVER_TIMEOUT) {
                createNotification(draft, {
                    _type: 'TimeoutNotification',
                    payload: action.payload
                });
            }
    }
}, INITIAL_STATE);
